import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/utils/cn'
import { useTranslation } from '@/i18n'

interface ThemeToggleProps {
  variant?: 'button' | 'dropdown'
  className?: string
}

export function ThemeToggle({ variant = 'button', className }: ThemeToggleProps) {
  const { theme, actualTheme, setTheme } = useTheme()
  const { t } = useTranslation()

  if (variant === 'dropdown') {
    return (
      <div className={cn('relative', className)}>
        <select
          value={theme}
          onChange={(e) => setTheme(e.target.value as any)}
          className="appearance-none bg-surface border border-border rounded-full px-3 py-2 pr-8 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="light">{t('theme.light')}</option>
          <option value="dark">{t('theme.dark')}</option>
          <option value="system">{t('theme.system')}</option>
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    )
  }

  const handleToggle = () => {
    if (theme === 'light') {
      setTheme('dark')
    } else if (theme === 'dark') {
      setTheme('system')
    } else {
      setTheme('light')
    }
  }

  const getIcon = () => {
    if (theme === 'system') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    }
    
    if (actualTheme === 'dark') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )
    }
    
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )
  }

  const getTooltip = () => {
    switch (theme) {
      case 'light':
        return t('theme.lightMode')
      case 'dark':
        return t('theme.darkMode')
      case 'system':
        return t('theme.systemMode')
      default:
        return ''
    }
  }

  return (
    <button
      onClick={handleToggle}
      className={cn(
        'p-2 rounded-full bg-surface border border-border hover:bg-bg-alt transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        className
      )}
      title={getTooltip()}
      aria-label={getTooltip()}
    >
      <div className="text-text">
        {getIcon()}
      </div>
    </button>
  )
}

// Settings component for theme selection
export function ThemeSettings() {
  const { theme, setTheme } = useTheme()
  const { t } = useTranslation()

  const options = [
    { value: 'light', label: t('theme.light'), icon: '☀️' },
    { value: 'dark', label: t('theme.dark'), icon: '🌙' },
    { value: 'system', label: t('theme.system'), icon: '💻' }
  ]

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-xl">
      <h3 className="text-lg font-semibold text-text mb-lg">{t('theme.appearance')}</h3>
      <p className="text-text-muted mb-lg">{t('theme.selectTheme')}</p>
      
      <div className="space-y-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex items-center gap-3 p-3 rounded-full border cursor-pointer transition-colors',
              theme === option.value
                ? 'border-primary bg-primary/5'
                : 'border-border hover:bg-bg-alt'
            )}
          >
            <input
              type="radio"
              name="theme"
              value={option.value}
              checked={theme === option.value}
              onChange={(e) => setTheme(e.target.value as any)}
              className="sr-only"
            />
            <span className="text-xl">{option.icon}</span>
            <div className="flex-1">
              <div className="font-medium text-text">{option.label}</div>
              {option.value === 'system' && (
                <div className="text-sm text-text-muted">{t('theme.systemDescription')}</div>
              )}
            </div>
            <div className={cn(
              'w-4 h-4 rounded-full border-2',
              theme === option.value
                ? 'border-primary bg-primary'
                : 'border-border'
            )}>
              {theme === option.value && (
                <div className="w-full h-full rounded-full bg-white dark:bg-surface scale-50" />
              )}
            </div>
          </label>
        ))}
      </div>
    </div>
  )
}